import React, { useMemo, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './MobileRequestDetails.scss';
import { AskRequest } from '@hone-automation/common/lib/AskRequests';
import { useQueueRequestsQuery } from 'hooks/useQueueRequestsQuery';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import Chat from 'presentation/components/Queue/Chat/Chat';
import CMComposer from 'presentation/components/Queue/CMComposer/CMComposer';
import { MessageList } from 'presentation/components/KSQ2/Panels/ConversationPanel';
import { prefixClass } from 'lib/utils';
import classNames from 'classnames';
import {
  IconBackward,
  IconMoreVertical,
  IconKaiSynth,
  IconCell,
  IconBlockerAlert,
  IconPendoInfo,
  IconSolved,
} from 'components/Icons';
import StatusItem from 'presentation/components/Queue/common/StatusItem/StatusItem';
import { format } from 'date-fns';
import { RequestStatus } from 'presentation/components/Queue/RequestItem/type';
import MobileActionModal from 'presentation/components/Mobile/MobileActionModal/MobileActionModal';
import { MobileSlideUpPanel } from 'presentation/components/Mobile/MobileSlideUpPanel/MobileSlideUpPanel';
import RequestInfoPanel from './panels/RequestInfoPanel';
import RequestSummarizePanel from './panels/RequestSummarizePanel';
import MobileRecategorizePanel from './panels/MobileRecategorizePanel';
import MobileMarkAsSolvedPanel from './panels/MobileMarkAsSolvedPanel';
import QueueTogglePriorityButton from 'presentation/components/Queue/QueueDetails/QueueTogglePriorityButton';

import { useAuth, useAuthContext } from 'context/useAuthContext';
import { HoneAbilitySubjects } from '@hone-automation/common';

// Augment AskRequest type to include priority field
import { AskRequest as BaseAskRequest } from '@hone-automation/common/lib/AskRequests';
interface EnhancedAskRequest extends BaseAskRequest {
  priority: 'Blocker' | 'Normal';
}

const STATUS_MAP: { [key: string]: RequestStatus } = {
  Open: 'created',
  'In Progress': 'inProgress',
  Closed: 'resolved',
};

type Props = {
  requestId: string;
  onClose: () => void;
  className?: string;
};

import { MobileActionOption } from 'presentation/components/Mobile/MobileActionModal/MobileActionModal';
import { CMComposerContexts } from 'presentation/components/Queue/CMComposer/types';

// SWAP FLAG: Set to true to test new MessageList component, false to use old Chat component
const USE_NEW_MESSAGE_LIST = true;

function getRequestTitle(request: AskRequest | undefined): string {
  if (!request) return 'Loading...';
  if (request.type === 'Customer Request') {
    return 'Customer Request';
  }
  if (request.transaction) {
    return request.transaction.id || 'Transaction Request';
  }
  return `Request ${request.id.substring(0, 6)}`;
}

function MobileRequestDetails({ requestId, onClose, className }: Props): JSX.Element | null {
  const { data: requests, isLoading, isError, error } = useQueueRequestsQuery(undefined, true);
  const { state: queueState } = useQueueContext();
  const prefix = prefixClass('mobile-request-details');
  const { isBookkeeperAdmin, currentLocationAbility } = useAuthContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
  const [isSummarizePanelOpen, setIsSummarizePanelOpen] = useState(false);
  const [isRecategorizePanelOpen, setIsRecategorizePanelOpen] = useState(false);
  const [isMarkAsSolvedPanelOpen, setIsMarkAsSolvedPanelOpen] = useState(false);
  const moreButtonRef = useRef<HTMLDivElement>(null);

  // First try to get the request from QueueContext (which will be updated in real-time)
  // If not found there, fall back to the query results
  const selectedRequest = useMemo(() => {
    // If the selected item in QueueContext matches our requestId, use that (it will be most up-to-date)
    if (queueState.selectedItem && queueState.selectedItem.id === requestId) {
      return queueState.selectedItem;
    }
    // Otherwise, try to find it in the QueueContext requests array
    const contextRequest = queueState.requests.find(req => req.id === requestId);
    if (contextRequest) {
      return contextRequest;
    }
    // Finally, fall back to the query results
    return requests?.find(req => req.id === requestId);
  }, [queueState.selectedItem, queueState.requests, requests, requestId]);

  // Cast selectedItem to EnhancedAskRequest to access the priority property
  const enhancedSelectedItem = selectedRequest as EnhancedAskRequest;
  const { transaction, status, comments, createdAt, locationId, canRecategorize, id, priority } =
    enhancedSelectedItem || ({} as EnhancedAskRequest);

  const modalOptions = useMemo(() => {
    const options: MobileActionOption[] = [];

    if (!selectedRequest) return options;

    if (isBookkeeperAdmin && transaction?.id && status !== 'Closed' && canRecategorize) {
      options.push({
        name: 'Recategorize',
        onClick: () => {
          setIsActionModalOpen(false);
          setIsRecategorizePanelOpen(true);
        },
      });
    }

    // Only add toggle priority if user has permission
    if (currentLocationAbility?.can('manage', HoneAbilitySubjects.Queue)) {
      options.push({
        name: 'Toggle Priority',
        children: <QueueTogglePriorityButton id={id} currentPriority={priority} unflagOnly={status === 'Closed'} />,
        onClick: () => {
          // This will run when the list item is clicked, before the modal closes
          // The QueueTogglePriorityButton's own click handler will still work
          // We're just adding this to update the URL params for when the user goes back

          // Wait a short time to let the toggle action complete
          setTimeout(() => {
            // Determine the new priority (opposite of current)
            const newPriority = priority === 'Blocker' ? 'Normal' : 'Blocker';

            // Update URL params to reflect the new priority
            const params = new URLSearchParams(searchParams);

            if (newPriority === 'Blocker') {
              // If becoming a blocker, set priority param and keep status
              params.set('priority', 'Blocker');
            } else {
              // If becoming normal, remove priority param and keep status
              params.delete('priority');
            }

            // Update the URL without navigating (for when user goes back)
            navigate(`?${params.toString()}`, { replace: true });
          }, 100); // Small delay to let the toggle action complete
        },
      });
    }

    if (status !== 'Closed') {
      options.push({
        name: 'Mark as Solved',
        onClick: () => {
          setIsActionModalOpen(false);
          setIsMarkAsSolvedPanelOpen(true);
        },
      });
    }

    return options;
  }, [
    selectedRequest,
    isBookkeeperAdmin,
    transaction,
    status,
    id,
    locationId,
    priority,
    currentLocationAbility,
    canRecategorize,
    searchParams,
    navigate,
  ]);

  const displayLoading = isLoading;
  const displayError = isError || (!isLoading && !selectedRequest);
  const errorMessage = error?.message || (!isLoading && !selectedRequest ? 'Request not found.' : 'Unknown error');

  const createdDate = createdAt ? format(new Date(createdAt), 'MM/dd/yyyy') : '-';
  const createdByUser =
    comments?.[0]?.user?.name ||
    comments?.[0]?.userLocation?.email?.split('@')[0] ||
    comments?.[0]?.userLocation?.role ||
    'Unknown';
  const latestStatus = comments
    ?.slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  const updatedDate = latestStatus?.createdAt ? format(new Date(latestStatus.createdAt), 'MM/dd/yyyy') : '-';
  const updatedByUser =
    latestStatus?.user?.name ||
    latestStatus?.userLocation?.email?.split('@')[0] ||
    latestStatus?.userLocation?.role ||
    'Unknown';

  // Function to update URL parameters based on current request state
  const updateUrlParamsForList = () => {
    if (!selectedRequest) return;

    const params = new URLSearchParams(searchParams);

    // Update priority parameter
    if (priority === 'Blocker') {
      params.set('priority', 'Blocker');
    } else {
      params.delete('priority');
    }

    // Update status parameter based on current status
    if (status) {
      params.set('status', status);
    }

    // Add requestId parameter to identify which request to scroll to
    params.set('requestId', requestId);

    // Update the URL without navigating
    navigate(`?${params.toString()}`, { replace: true });
  };

  // Enhanced onClose handler
  const handleBackButtonClick = () => {
    // Update URL parameters before closing
    updateUrlParamsForList();
    // Call the original onClose prop
    onClose();
  };

  return (
    <div className={`MobileRequestDetails ${className || ''}`}>
      <div className={classNames('MobileRequestDetails-header', { blocker: priority === 'Blocker' })}>
        <div onClick={handleBackButtonClick} className="MobileRequestDetails-header-left-side">
          <div className="MobileRequestDetails-header-icon">
            <IconBackward />
          </div>
          <StatusItem userName={updatedByUser} status={STATUS_MAP[status]} date={updatedDate} showDate />
        </div>
        <div className="MobileRequestDetails-header-options">
          {priority === 'Blocker' && (
            <div className="MobileRequestDetails-header-icon-blocker">
              <IconBlockerAlert />
            </div>
          )}
          <div onClick={() => setIsSummarizePanelOpen(true)} className="MobileRequestDetails-header-icon">
            <IconKaiSynth />
          </div>
          <div onClick={() => setIsInfoPanelOpen(true)} className="MobileRequestDetails-header-icon">
            <IconPendoInfo />
          </div>
          <div
            ref={moreButtonRef}
            onClick={() => setIsActionModalOpen(true)}
            className="MobileRequestDetails-header-icon"
          >
            <IconMoreVertical />
          </div>
        </div>
      </div>
      <div className="MobileRequestDetails-content">
        {displayLoading && (
          <div className="loading-indicator" style={{ padding: '20px', textAlign: 'center' }}>
            <p>Loading...</p>
          </div>
        )}
        {displayError && !displayLoading && (
          <div className="error-message" style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
            <p>Error: {errorMessage}</p>
          </div>
        )}
        {!displayLoading && !displayError && selectedRequest && (
          <div className={prefix()}>
            <div className={prefix('messages')}>
              {comments &&
                (USE_NEW_MESSAGE_LIST ? (
                  <MessageList request={selectedRequest} comments={comments} mobile={true} />
                ) : (
                  <Chat comments={comments} mobile={true} />
                ))}
            </div>
            <CMComposer request={selectedRequest} mobile={true} context={CMComposerContexts.queue} />
          </div>
        )}
      </div>

      {/* Action Modal */}
      <MobileActionModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        title="Actions"
        triggerRef={moreButtonRef}
        options={modalOptions}
      />

      {/* Info Panel */}
      {selectedRequest && (
        <RequestInfoPanel
          isOpen={isInfoPanelOpen}
          onClose={() => setIsInfoPanelOpen(false)}
          requestId={requestId}
          titleIcon={<IconPendoInfo />}
        />
      )}

      {/* Summarize Panel */}
      {selectedRequest && (
        <RequestSummarizePanel
          isOpen={isSummarizePanelOpen}
          onClose={() => setIsSummarizePanelOpen(false)}
          requestId={requestId}
          titleIcon={<IconKaiSynth />}
        />
      )}

      {/* New mobile-specific panels */}
      {selectedRequest && (
        <>
          <MobileRecategorizePanel
            isOpen={isRecategorizePanelOpen}
            onClose={() => setIsRecategorizePanelOpen(false)}
            txId={selectedRequest.id}
            locationId={locationId || ''}
            activeTab={status}
            titleIcon={<IconCell />}
          />

          <MobileMarkAsSolvedPanel
            isOpen={isMarkAsSolvedPanelOpen}
            onClose={() => setIsMarkAsSolvedPanelOpen(false)}
            titleIcon={<IconSolved />}
            txId={selectedRequest.id}
            locationId={locationId || ''}
            activeTab={status}
          />
        </>
      )}
    </div>
  );
}

export default MobileRequestDetails;
